import type { BookingWizardCustomer } from "api-client";
import { ApiErrorHandler, shouldSuppressApiErrorNotification } from "api-client";
import { useCallback, useEffect, useState } from "react";
import { bookingsService } from "../services/bookings.service";

export interface UseBookingWizardCustomersOptions {
  /** Fetch when true (e.g. while create-booking drawer is open). */
  enabled?: boolean;
  /** Logged-in operator — sent as `operatorId` on GET /api/bookings/customers. */
  operatorId: number;
}

export interface UseBookingWizardCustomersResult {
  customers: BookingWizardCustomer[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Customers + nested locations for the create-booking wizard (`GET .../bookings/customers`).
 */
export function useBookingWizardCustomers(
  options: UseBookingWizardCustomersOptions,
): UseBookingWizardCustomersResult {
  const { enabled = true, operatorId } = options;

  const [customers, setCustomers] = useState<BookingWizardCustomer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCustomers = useCallback(async () => {
    if (!Number.isFinite(operatorId)) {
      setCustomers([]);
      setError("Operator id is required to load customers.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await bookingsService.getBookingWizardCustomers({ operatorId });
      setCustomers(response.data?.customers ?? []);
    } catch (err: unknown) {
      if (shouldSuppressApiErrorNotification(err)) {
        return;
      }
      setError(ApiErrorHandler.extractAxiosError(err, "Failed to load customers"));
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, [operatorId]);

  useEffect(() => {
    if (enabled) {
      void fetchCustomers();
    }
  }, [enabled, fetchCustomers]);

  return {
    customers,
    loading,
    error,
    refetch: fetchCustomers,
  };
}
